import React, { useState, useEffect, useRef, useMemo } from 'react';
import { hasAnyArrayData } from '../../utils/profileVisibility';
import SectionEditModal from './SectionEditModal';
import EditIconButton from './EditIconButton';
import { unfurlTikTokThumbnail, isLikelyTikTokUrl, normalizeTikTokUrl } from '../../lib/featuredPosts/unfurlTikTokClient';
import { normalizeFeaturedPosts, groupFeaturedPostsByPlatform, getAvailablePlatforms, PLATFORM_LABELS } from '../../lib/featuredPosts/normalizeFeaturedPosts';
import { formatPlatformLabel, getConnectedPlatformsFromUserDoc, normalizePlatform } from '../../lib/socials';
import WorkingFeaturedPostEmbed from './WorkingFeaturedPostEmbed';
import PlatformSelectDropdown from './PlatformSelectDropdown';
import TikTokIcon from '../icons/TikTokIcon';
import InstagramIcon from '../icons/InstagramIcon';
import YouTubeIcon from '../icons/YouTubeIcon';

/**
 * Fetch thumbnail URL from any social media URL using Open Graph meta tags
 * This is a generic solution that works across all platforms
 */
const fetchThumbnail = async (url) => {
  if (!url || !url.startsWith('http')) {
    return null;
  }

  try {
    const res = await fetch('/api/link-preview', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url }),
    });

    if (!res.ok) {
      console.warn('[fetchThumbnail] API request failed:', res.status);
      return null;
    }

    const data = await res.json();
    return data.thumbnailUrl ?? null;
  } catch (e) {
    console.error('[fetchThumbnail] Failed to fetch thumbnail:', e);
    return null;
  }
};

/**
 * Generate thumbnail URL from video URL based on platform
 * Falls back to YouTube-specific pattern for faster loading
 */
function generateThumbnailFromUrl(videoUrl, platform) {
  if (!videoUrl) return '';

  try {
    // YouTube has a reliable thumbnail pattern we can use immediately
    if (platform === 'youtube') {
      const match = videoUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
      if (match && match[1]) {
        return `https://img.youtube.com/vi/${match[1]}/maxresdefault.jpg`;
      }
    }
    // For other platforms, we'll use the generic link preview API
    // Return empty here - thumbnail will be fetched via fetchThumbnail
  } catch (err) {
    console.error('Error generating thumbnail URL:', err);
  }

  return '';
}

const PLATFORM_ICONS = {
  tiktok: '🎵',
  instagram: '📷',
  youtube: '▶️',
};

const MAX_FEATURED_POSTS = 9;

function FeaturedPostsSection({ featuredPosts, isOwner, onSaveFeaturedPosts, mode = 'edit', profile, userDoc }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formPosts, setFormPosts] = useState([]);
  const [newPost, setNewPost] = useState({
    platform: '',
    title: '',
    contentUrl: '',
    thumbnailUrl: '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [unfurlLoading, setUnfurlLoading] = useState(false);
  const [unfurlError, setUnfurlError] = useState(null);
  const debounceTimerRef = useRef(null);
  const unfurlCacheRef = useRef(new Map());
  // Per-post loading/error states
  const [postUnfurlStates, setPostUnfurlStates] = useState({});
  // Selected platform filter for viewer
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  // Selected platform for editor
  const [editorPlatform, setEditorPlatform] = useState('tiktok');
  const [tiktokEmbedVersion, setTiktokEmbedVersion] = useState(0);

  const viewerPlatforms = useMemo(() => {
    const normalized = normalizeFeaturedPosts(featuredPosts || []);
    return getAvailablePlatforms(groupFeaturedPostsByPlatform(normalized));
  }, [featuredPosts]);

  // Prefer profile.socialLinks (influencer/formData) so all connected platforms show (works for any future socials)
  const connectedPlatforms = useMemo(() => {
    const doc = profile || userDoc || {};
    const links = doc.socialLinks;
    if (links && typeof links === 'object' && !Array.isArray(links)) {
      const withValue = (key) => links[key] && String(links[key]).trim();
      const keys = Object.keys(links).filter(withValue);
      if (keys.length === 0) return getConnectedPlatformsFromUserDoc(userDoc || profile || {});
      const order = ['tiktok', 'instagram', 'youtube', 'twitter', 'linkedin', 'facebook', 'snapchat', 'pinterest', 'website', 'other'];
      const ordered = order.filter((p) => keys.includes(p));
      const rest = keys.filter((k) => !order.includes(k)).sort();
      return [...ordered, ...rest];
    }
    return getConnectedPlatformsFromUserDoc(userDoc || profile || {});
  }, [profile, userDoc, profile?.socialLinks]);

  const platformSelectOptions = useMemo(() => {
    const iconMap = {
      tiktok: <TikTokIcon className="h-4 w-4 text-slate-700" />,
      instagram: <InstagramIcon className="h-4 w-4 text-slate-700" />,
      youtube: <YouTubeIcon className="h-4 w-4 text-slate-700" />,
    };

    return connectedPlatforms.map((platform) => ({
      key: platform,
      label: PLATFORM_LABELS[platform] || formatPlatformLabel(platform) || (platform ? platform.charAt(0).toUpperCase() + platform.slice(1).toLowerCase() : ''),
      icon: iconMap[platform] || <span className="h-4 w-4 rounded-full bg-slate-400" />,
    }));
  }, [connectedPlatforms]);

  const defaultEditorPlatform = useMemo(() => {
    const normalized = normalizeFeaturedPosts(featuredPosts || []);
    const byPlatform = groupFeaturedPostsByPlatform(normalized);
    for (const platform of connectedPlatforms) {
      if ((byPlatform[platform] || []).length > 0) {
        return platform;
      }
    }
    return connectedPlatforms[0] || '';
  }, [connectedPlatforms, featuredPosts]);

  useEffect(() => {
    if (viewerPlatforms.length === 0) {
      if (selectedPlatform !== null) {
        setSelectedPlatform(null);
      }
      return;
    }

    if (!selectedPlatform || !viewerPlatforms.includes(selectedPlatform)) {
      setSelectedPlatform(viewerPlatforms[0]);
    }
  }, [viewerPlatforms, selectedPlatform]);

  const viewerActivePlatform = selectedPlatform && viewerPlatforms.includes(selectedPlatform)
    ? selectedPlatform
    : (viewerPlatforms[0] || null);

  useEffect(() => {
    if (viewerActivePlatform !== 'tiktok') return;

    const triggerTikTokEmbeds = () => {
      if (window?.tiktok?.embed?.load) {
        window.tiktok.embed.load();
        return;
      }
      if (window?.tiktok?.embed) {
        window.tiktok.embed();
      }
    };

    const timers = [
      setTimeout(triggerTikTokEmbeds, 0),
      setTimeout(triggerTikTokEmbeds, 300),
      setTimeout(triggerTikTokEmbeds, 900),
    ];

    setTiktokEmbedVersion((prev) => prev + 1);

    return () => timers.forEach(clearTimeout);
  }, [viewerActivePlatform, featuredPosts]);

  useEffect(() => {
    if (isModalOpen) {
      const initial = (featuredPosts || []).map((p) => ({
        id: p.id || String(Date.now() + Math.random()),
        platform: normalizePlatform(p.platform || '') || p.platform || '',
        title: p.title || '',
        contentUrl: p.contentUrl || p.url || '', // Support both url (normalized) and contentUrl (legacy)
        thumbnailUrl: p.thumbnailUrl || '',
      }));
      setEditorPlatform(defaultEditorPlatform);
      setFormPosts(initial);
      setNewPost({
        platform: '',
        title: '',
        contentUrl: '',
        thumbnailUrl: '',
      });
      setUnfurlLoading(false);
      setUnfurlError(null);
      setPostUnfurlStates({});
      setError(null);
    }
  }, [featuredPosts, isModalOpen, defaultEditorPlatform]);

  useEffect(() => {
    if (!isModalOpen) return;
    if (connectedPlatforms.length === 0) {
      setEditorPlatform('');
      return;
    }
    if (!connectedPlatforms.includes(editorPlatform)) {
      setEditorPlatform(defaultEditorPlatform);
    }
  }, [isModalOpen, connectedPlatforms, editorPlatform, defaultEditorPlatform]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Auto-generate thumbnail when video URL changes (with debouncing for TikTok)
  const handleVideoUrlChange = (videoUrl, platform) => {
    // Clear any existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    // Try platform-specific pattern first (e.g., YouTube)
    let generatedThumbnail = generateThumbnailFromUrl(videoUrl, platform);
    
    // If we have a YouTube thumbnail, use it immediately
    if (generatedThumbnail) {
      setNewPost((prev) => ({
        ...prev,
        contentUrl: videoUrl,
        thumbnailUrl: prev.thumbnailUrl || generatedThumbnail, // Don't overwrite user input
      }));
      return;
    }

    // For TikTok, use specialized unfurl with debouncing
    if (platform === 'tiktok' && isLikelyTikTokUrl(videoUrl)) {
      const normalizedUrl = normalizeTikTokUrl(videoUrl);
      
      // Check cache first
      const cached = unfurlCacheRef.current.get(normalizedUrl);
      if (cached) {
        setNewPost((prev) => ({
          ...prev,
          contentUrl: videoUrl,
          thumbnailUrl: prev.thumbnailUrl || cached.thumbnailUrl || '', // Don't overwrite user input
          title: prev.title || cached.title || '', // Only set title if empty
        }));
        return;
      }

      // Only auto-generate if thumbnailUrl is empty
      setNewPost((prev) => {
        if (prev.thumbnailUrl) {
          // User has typed something, don't auto-generate
          return { ...prev, contentUrl: videoUrl };
        }
        
        setUnfurlError(null);
        
        // Debounce the unfurl call (800ms)
        debounceTimerRef.current = setTimeout(async () => {
          setUnfurlLoading(true);
          try {
            const result = await unfurlTikTokThumbnail(normalizedUrl);
            
            // Cache the result
            if (normalizedUrl) {
              unfurlCacheRef.current.set(normalizedUrl, result);
            }
            
            // Only update if thumbnailUrl is still empty (user didn't type something)
            setNewPost((prev) => {
              if (prev.thumbnailUrl) {
                return prev; // Don't overwrite if user has typed something
              }
              return {
                ...prev,
                thumbnailUrl: result.thumbnailUrl || '',
                title: result.title && !prev.title ? result.title : prev.title,
              };
            });
            
            if (!result.thumbnailUrl) {
              setUnfurlError('Couldn\'t fetch preview. You can still save this post.');
            }
          } catch (err) {
            console.warn('Failed to unfurl TikTok:', err);
            setUnfurlError('Couldn\'t fetch preview. You can still save this post.');
          } finally {
            setUnfurlLoading(false);
          }
        }, 800);
        
        return { ...prev, contentUrl: videoUrl };
      });
      return;
    }

    // For other platforms, use generic link preview API (no debouncing needed)
    if (videoUrl && videoUrl.startsWith('http')) {
      setNewPost((prev) => ({ ...prev, contentUrl: videoUrl }));
      
      if (!newPost.thumbnailUrl) {
        fetchThumbnail(videoUrl).then((generatedThumbnail) => {
          if (generatedThumbnail) {
            setNewPost((prev) => {
              if (prev.thumbnailUrl) {
                return prev; // Don't overwrite if user has typed something
              }
              return {
                ...prev,
                thumbnailUrl: generatedThumbnail,
              };
            });
          }
        }).catch((err) => {
          console.warn('Failed to fetch thumbnail:', err);
        });
      }
    } else {
      setNewPost((prev) => ({ ...prev, contentUrl: videoUrl }));
    }
  };

  // Handle blur event - trigger unfurl immediately
  const handleVideoUrlBlur = async (videoUrl, platform) => {
    // Clear debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    // If it's TikTok and thumbnail is empty, try unfurl immediately
    if (platform === 'tiktok' && isLikelyTikTokUrl(videoUrl) && !newPost.thumbnailUrl) {
      const normalizedUrl = normalizeTikTokUrl(videoUrl);
      
      // Check cache first
      const cached = unfurlCacheRef.current.get(normalizedUrl);
      if (cached) {
        setNewPost((prev) => ({
          ...prev,
          thumbnailUrl: prev.thumbnailUrl || cached.thumbnailUrl || '',
          title: prev.title || cached.title || '',
        }));
        return;
      }

      setUnfurlLoading(true);
      setUnfurlError(null);
      try {
        const result = await unfurlTikTokThumbnail(normalizedUrl);
        
        // Cache the result
        if (normalizedUrl) {
          unfurlCacheRef.current.set(normalizedUrl, result);
        }
        
        setNewPost((prev) => {
          if (prev.thumbnailUrl) {
            return prev; // Don't overwrite if user has typed something
          }
          return {
            ...prev,
            thumbnailUrl: result.thumbnailUrl || '',
            title: result.title && !prev.title ? result.title : prev.title,
          };
        });
        
        if (!result.thumbnailUrl) {
          setUnfurlError('Couldn\'t fetch preview. You can still save this post.');
        }
      } catch (err) {
        console.warn('Failed to unfurl TikTok on blur:', err);
        setUnfurlError('Couldn\'t fetch preview. You can still save this post.');
      } finally {
        setUnfurlLoading(false);
      }
    }
  };

  const handlePlatformChange = async (platform) => {
    // Try platform-specific pattern first (e.g., YouTube)
    let generatedThumbnail = generateThumbnailFromUrl(newPost.contentUrl, platform);
    
    // If no platform-specific thumbnail, use generic link preview API
    if (!generatedThumbnail && newPost.contentUrl && newPost.contentUrl.startsWith('http')) {
      try {
        generatedThumbnail = await fetchThumbnail(newPost.contentUrl);
        console.log('Fetched thumbnail on platform change:', generatedThumbnail);
      } catch (err) {
        console.warn('Failed to fetch thumbnail:', err);
      }
    }

    setNewPost((prev) => ({
      ...prev,
      platform,
      thumbnailUrl: generatedThumbnail || prev.thumbnailUrl || '',
    }));
  };

  // Helper to handle TikTok unfurl for a specific post in formPosts
  const handlePostUrlChange = (idx, value, platform) => {
    // Try platform-specific pattern first (e.g., YouTube)
    let generatedThumbnail = generateThumbnailFromUrl(value, platform);
    
    // If we have a YouTube thumbnail, use it immediately
    if (generatedThumbnail) {
      setFormPosts((prev) => {
        const copy = [...prev];
        copy[idx] = { 
          ...copy[idx], 
          contentUrl: value,
          thumbnailUrl: copy[idx].thumbnailUrl || generatedThumbnail, // Don't overwrite user input
        };
        return copy;
      });
      return;
    }

    // For TikTok, use specialized unfurl with debouncing
    if (platform === 'tiktok' && isLikelyTikTokUrl(value)) {
      const normalizedUrl = normalizeTikTokUrl(value);
      
      // Check cache first
      const cached = unfurlCacheRef.current.get(normalizedUrl);
      if (cached) {
        setFormPosts((prev) => {
          const copy = [...prev];
          copy[idx] = { 
            ...copy[idx], 
            contentUrl: value,
            thumbnailUrl: copy[idx].thumbnailUrl || cached.thumbnailUrl || '', // Don't overwrite user input
            title: copy[idx].title || cached.title || '', // Only set title if empty
          };
          return copy;
        });
        return;
      }

      // Only auto-generate if thumbnailUrl is empty
      setFormPosts((prev) => {
        const copy = [...prev];
        const post = copy[idx];
        if (post.thumbnailUrl) {
          // User has typed something, don't auto-generate
          copy[idx] = { ...post, contentUrl: value };
          return copy;
        }
        
        // Set loading state for this post
        setPostUnfurlStates((states) => ({
          ...states,
          [idx]: { loading: true, error: null },
        }));
        
        // Debounce the unfurl call (800ms)
        setTimeout(async () => {
          try {
            const result = await unfurlTikTokThumbnail(normalizedUrl);
            
            // Cache the result
            if (normalizedUrl) {
              unfurlCacheRef.current.set(normalizedUrl, result);
            }
            
            // Only update if thumbnailUrl is still empty (user didn't type something)
            setFormPosts((prev) => {
              const copy = [...prev];
              const post = copy[idx];
              if (post.thumbnailUrl) {
                return prev; // Don't overwrite if user has typed something
              }
              copy[idx] = {
                ...post,
                thumbnailUrl: result.thumbnailUrl || '',
                title: result.title && !post.title ? result.title : post.title,
              };
              return copy;
            });
            
            setPostUnfurlStates((states) => ({
              ...states,
              [idx]: { loading: false, error: result.thumbnailUrl ? null : 'Couldn\'t fetch preview. You can still save this post.' },
            }));
          } catch (err) {
            console.warn('Failed to unfurl TikTok:', err);
            setPostUnfurlStates((states) => ({
              ...states,
              [idx]: { loading: false, error: 'Couldn\'t fetch preview. You can still save this post.' },
            }));
          }
        }, 800);
        
        copy[idx] = { ...post, contentUrl: value };
        return copy;
      });
      return;
    }

    // For other platforms, use generic link preview API
    setFormPosts((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], contentUrl: value };
      return copy;
    });

    if (value && value.startsWith('http') && !formPosts[idx].thumbnailUrl) {
      fetchThumbnail(value).then((generatedThumbnail) => {
        if (generatedThumbnail) {
          setFormPosts((prev) => {
            const copy = [...prev];
            const post = copy[idx];
            if (post.thumbnailUrl) {
              return prev; // Don't overwrite if user has typed something
            }
            copy[idx] = { ...post, thumbnailUrl: generatedThumbnail };
            return copy;
          });
        }
      }).catch((err) => {
        console.warn('Failed to fetch thumbnail:', err);
      });
    }
  };

  // Handle blur for post URL
  const handlePostUrlBlur = async (idx, value, platform) => {
    // If it's TikTok and thumbnail is empty, try unfurl immediately
    if (platform === 'tiktok' && isLikelyTikTokUrl(value) && !formPosts[idx]?.thumbnailUrl) {
      const normalizedUrl = normalizeTikTokUrl(value);
      
      // Check cache first
      const cached = unfurlCacheRef.current.get(normalizedUrl);
      if (cached) {
        setFormPosts((prev) => {
          const copy = [...prev];
          const post = copy[idx];
          copy[idx] = {
            ...post,
            thumbnailUrl: post.thumbnailUrl || cached.thumbnailUrl || '',
            title: post.title || cached.title || '',
          };
          return copy;
        });
        return;
      }

      setPostUnfurlStates((states) => ({
        ...states,
        [idx]: { loading: true, error: null },
      }));

      try {
        const result = await unfurlTikTokThumbnail(normalizedUrl);
        
        // Cache the result
        if (normalizedUrl) {
          unfurlCacheRef.current.set(normalizedUrl, result);
        }
        
        setFormPosts((prev) => {
          const copy = [...prev];
          const post = copy[idx];
          if (post.thumbnailUrl) {
            return prev; // Don't overwrite if user has typed something
          }
          copy[idx] = {
            ...post,
            thumbnailUrl: result.thumbnailUrl || '',
            title: result.title && !post.title ? result.title : post.title,
          };
          return copy;
        });
        
        setPostUnfurlStates((states) => ({
          ...states,
          [idx]: { loading: false, error: result.thumbnailUrl ? null : 'Couldn\'t fetch preview. You can still save this post.' },
        }));
      } catch (err) {
        console.warn('Failed to unfurl TikTok on blur:', err);
        setPostUnfurlStates((states) => ({
          ...states,
          [idx]: { loading: false, error: 'Couldn\'t fetch preview. You can still save this post.' },
        }));
      }
    }
  };

  const handleSave = async () => {
    setError(null);

    try {
      setIsSaving(true);

      const cleaned = [];

      for (const post of formPosts) {
        const id = post.id || String(Date.now() + Math.random());
        const contentUrl = (post.contentUrl || '').trim();
        if (!contentUrl) {
          // skip posts with no content URL
          continue;
        }

        // Auto-generate thumbnail if not provided
        let thumbnailUrl = (post.thumbnailUrl || '').trim();
        if (!thumbnailUrl) {
          // Try platform-specific pattern first (e.g., YouTube)
          thumbnailUrl = generateThumbnailFromUrl(contentUrl, post.platform);
          
          // If no platform-specific thumbnail, use generic link preview API
          if (!thumbnailUrl && contentUrl.startsWith('http')) {
            try {
              thumbnailUrl = await fetchThumbnail(contentUrl);
              console.log('Fetched thumbnail on save:', thumbnailUrl);
            } catch (err) {
              console.warn('Failed to fetch thumbnail on save:', err);
            }
          }
        }

        // Validate thumbnail URL - must be a valid HTTP(S) URL or empty
        if (thumbnailUrl && !thumbnailUrl.startsWith('http://') && !thumbnailUrl.startsWith('https://')) {
          thumbnailUrl = '';
        }

        const postData = {
          id,
          platform: (post.platform || '').trim() || undefined,
          title: (post.title || '').trim() || undefined,
          contentUrl,
        };
        
        if (thumbnailUrl && thumbnailUrl.trim()) {
          postData.thumbnailUrl = thumbnailUrl.trim();
        }
        
        cleaned.push(postData);
      }

      await onSaveFeaturedPosts(cleaned);
      setIsModalOpen(false);
      setNewPost({
        platform: '',
        title: '',
        contentUrl: '',
        thumbnailUrl: '',
      });
      setError(null);
    } catch (e) {
      console.error('Error saving featured posts:', e);
      setError(e.message || 'Failed to save featured posts. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setError(null);
    // Reset form from props
    const reset = (featuredPosts || []).map((p) => ({
      id: p.id || String(Date.now() + Math.random()),
      platform: p.platform || '',
      title: p.title || '',
      contentUrl: p.contentUrl || '',
      thumbnailUrl: p.thumbnailUrl || '',
    }));
    setFormPosts(reset);
    setNewPost({
      platform: '',
      title: '',
      contentUrl: '',
      thumbnailUrl: '',
    });
    setUnfurlLoading(false);
    setUnfurlError(null);
    setPostUnfurlStates({});
  };

  // EDIT MODE - URL-ONLY EDITOR (NO TITLE/THUMBNAIL UI)
  // VIEW MODE
  const renderViewMode = () => {
    const normalizedPosts = normalizeFeaturedPosts(featuredPosts || []);
    const byPlatform = groupFeaturedPostsByPlatform(normalizedPosts);
    const availablePlatforms = viewerPlatforms;
    const hasPosts = normalizedPosts.length > 0;

    // Hide section if empty in public mode
    if (mode === 'public' && !hasPosts) {
      return null;
    }

    const activePlatform = viewerActivePlatform || availablePlatforms[0] || null;
    const embedKey = activePlatform ? `featured-posts-${activePlatform}` : 'featured-posts';

    const postsToShow = activePlatform
      ? (byPlatform[activePlatform] || []).slice(0, 3)
      : normalizedPosts.slice(0, 3);

    const totalCount = activePlatform
      ? (byPlatform[activePlatform] || []).length
      : normalizedPosts.length;

    return (
      <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 md:p-6 mb-5 md:mb-6 relative">
        <div className="mb-3 md:mb-4 flex items-center justify-between gap-2">
          <h2 className="text-lg md:text-xl font-semibold text-slate-900">
            Featured Posts
          </h2>
        </div>
        {isOwner && (
          <div className="absolute right-4 sm:right-6 top-4 sm:top-6 z-10">
            <EditIconButton onClick={() => setIsModalOpen(true)} />
          </div>
        )}

        {hasPosts ? (
          <>
            {/* Platform Chips */}
            {availablePlatforms.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {availablePlatforms.map((platform) => {
                  const isActive = platform === activePlatform;
                  return (
                    <button
                      key={platform}
                      onClick={() => setSelectedPlatform(platform)}
                      className={`
                        px-3 py-1.5 rounded-full text-sm font-medium transition-colors
                        ${isActive 
                          ? 'bg-slate-900 text-white' 
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }
                      `}
                    >
                      {PLATFORM_LABELS[platform] || platform}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Posts Grid */}
            <div key={embedKey} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
              {postsToShow.map((post) => (
                <WorkingFeaturedPostEmbed
                  key={post.url || post.id}
                  post={post}
                  embedVersion={tiktokEmbedVersion}
                />
              ))}
            </div>
          </>
        ) : (
          <p className="text-sm text-slate-500">
            No featured posts added yet. Highlight a few of your best TikToks, Reels, or Shorts here.
          </p>
        )}
      </section>
    );
  };
  const renderEditForm = () => {
    // Normalize all posts
    const normalizedPosts = normalizeFeaturedPosts(formPosts);
    const byPlatform = groupFeaturedPostsByPlatform(normalizedPosts);
    
    // Get posts for editor platform
    const platformPosts = editorPlatform ? (byPlatform[editorPlatform] || []) : [];
    const canAddMore = platformPosts.length < 3;
    const hasPlatforms = connectedPlatforms.length > 0;
    
    return (
      <div className="space-y-4">
        {!hasPlatforms ? (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            Connect a social account to add featured posts.
          </div>
        ) : (
          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block">
              Select Platform
            </label>
            <PlatformSelectDropdown
              value={editorPlatform}
              onChange={setEditorPlatform}
              options={platformSelectOptions}
            />
            <p className="mt-1 text-xs text-slate-500">
              Edit posts for one platform at a time. Max 3 posts per platform.
            </p>
          </div>
        )}

        {/* Post Counter */}
        <div className="flex items-center justify-between text-xs text-slate-600">
          <span className="font-medium uppercase tracking-wide">
            {editorPlatform ? `${PLATFORM_LABELS[editorPlatform] || formatPlatformLabel(editorPlatform)} Posts` : 'Posts'}
          </span>
          <span className={platformPosts.length >= 3 ? 'text-amber-600 font-medium' : ''}>
            {platformPosts.length} / 3 posts
          </span>
        </div>

        {/* Existing Posts for Platform (URL-ONLY LIST) */}
        {platformPosts.length > 0 && (
          <div className="space-y-2">
            {platformPosts.map((post) => {
              const originalIndex = formPosts.findIndex(p => 
                (p.id && p.id === post.id) || 
                (p.contentUrl && p.contentUrl === post.url) ||
                (p.url && p.url === post.url)
              );
              
              return (
                <div 
                  key={post.id}
                  className="border border-slate-200 rounded-lg p-3 flex items-center gap-3"
                >
                  {/* Platform Icon */}
                  <div className="flex-shrink-0 w-8 h-8 rounded bg-slate-100 flex items-center justify-center text-xs font-semibold text-slate-600">
                    {PLATFORM_ICONS[editorPlatform] || '🔗'}
                  </div>
                  
                  {/* URL Only */}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-slate-700 truncate">
                      {post.url}
                    </p>
                  </div>
                  
                  {/* Remove Button */}
                  <button
                    type="button"
                    onClick={() => {
                      if (originalIndex >= 0) {
                        setFormPosts((prev) => prev.filter((_, i) => i !== originalIndex));
                      }
                    }}
                    className="flex-shrink-0 text-red-600 hover:text-red-700 text-sm font-medium px-2 py-1"
                  >
                    Remove
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* No Posts Message */}
        {platformPosts.length === 0 && hasPlatforms && editorPlatform && (
          <p className="text-sm text-slate-500 text-center py-4 bg-slate-50 rounded-lg">
            No {PLATFORM_LABELS[editorPlatform] || formatPlatformLabel(editorPlatform)} posts yet. Add one below!
          </p>
        )}

        {/* Add New Post Section - URL ONLY */}
        <div className="border-t border-slate-200 pt-4 space-y-3">
          <h4 className="text-sm font-medium text-slate-700">
            {editorPlatform
              ? `Add ${PLATFORM_LABELS[editorPlatform] || formatPlatformLabel(editorPlatform)} Post`
              : 'Add Post'}
          </h4>
          
          {/* URL Input ONLY */}
          <div>
            <label className="text-xs font-medium text-slate-600">
              Content URL <span className="text-red-500">*</span>
            </label>
            <input
              type="url"
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              value={newPost.contentUrl}
              onChange={(e) => setNewPost((prev) => ({ ...prev, contentUrl: e.target.value }))}
              placeholder={`https://www.${editorPlatform}.com/...`}
            disabled={!canAddMore || !hasPlatforms || !editorPlatform}
            />
          </div>

          {/* Add Button */}
          <button
            type="button"
            disabled={!canAddMore || !newPost.contentUrl.trim() || !hasPlatforms || !editorPlatform}
            className={`w-full rounded-md px-4 py-2 text-sm font-medium ${
              !canAddMore || !newPost.contentUrl.trim() || !hasPlatforms || !editorPlatform
                ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                : 'bg-slate-900 text-white hover:bg-slate-800'
            }`}
            onClick={async () => {
              if (!hasPlatforms || !editorPlatform) {
                setError('Connect a social account to add featured posts.');
                return;
              }
              const trimmedUrl = newPost.contentUrl.trim();
              if (!trimmedUrl || !trimmedUrl.startsWith('http')) {
                setError('Please enter a valid URL starting with http:// or https://');
                return;
              }

              // Check for duplicates
              const isDuplicate = formPosts.some(p => {
                const pUrl = p.contentUrl || p.url || '';
                return pUrl.trim().toLowerCase() === trimmedUrl.toLowerCase();
              });

              if (isDuplicate) {
                setError('This URL is already added');
                return;
              }

              const id = String(Date.now() + Math.random());
              
              // Auto-fetch thumbnail (best effort, background)
              let thumbnailUrl = '';
              try {
                if (editorPlatform === 'tiktok' && isLikelyTikTokUrl(trimmedUrl)) {
                  const result = await unfurlTikTokThumbnail(trimmedUrl);
                  thumbnailUrl = result?.thumbnailUrl || '';
                } else {
                  thumbnailUrl = await fetchThumbnail(trimmedUrl) || '';
                }
              } catch (err) {
                console.warn('Thumbnail fetch failed:', err);
              }

              // Add to formPosts
              setFormPosts((prev) => [
                ...prev,
                {
                  id,
                  platform: editorPlatform,
                  contentUrl: trimmedUrl,
                  title: '', // No title input
                  thumbnailUrl,
                },
              ]);

              // Reset form
              setNewPost({ platform: '', title: '', contentUrl: '', thumbnailUrl: '' });
              setError(null);
            }}
          >
            {!canAddMore && editorPlatform
              ? `Maximum 3 ${PLATFORM_LABELS[editorPlatform] || formatPlatformLabel(editorPlatform)} posts reached`
              : 'Add Post'}
          </button>
          
          {!canAddMore && (
            <p className="text-xs text-amber-600">
              You've reached the maximum of 3 posts for {PLATFORM_LABELS[editorPlatform]}. Remove a post to add another.
            </p>
          )}
        </div>
      </div>
    );
  };
  return (
    <>
      {renderViewMode()}
      
      {isOwner && (
        <SectionEditModal
          isOpen={isModalOpen}
          onClose={handleCancel}
          title="Edit Featured Posts"
          subtitle="Highlight the content you want brands to see first."
          onSave={handleSave}
          isSaving={isSaving}
          disableSave={connectedPlatforms.length === 0}
          errors={error ? { _general: error } : {}}
        >
          {renderEditForm()}
        </SectionEditModal>
      )}
    </>
  );
}

export default FeaturedPostsSection;
